
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { ArrowDown, Code, Lightbulb, Users, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const NearshoreBadge = ({ isVisible }: { isVisible: boolean }) => (
  <div
    className={cn(
      "group relative inline-flex items-center gap-4 rounded-2xl px-5 py-3",
      "border border-primary/20 bg-white",
      "shadow-[0_10px_30px_-10px_rgba(37,99,235,0.3)] hover:shadow-[0_14px_36px_-10px_rgba(37,99,235,0.4)]",
      "transition-all duration-500 ease-out hover:-translate-y-0.5",
      isVisible ? "opacity-100" : "opacity-0"
    )}
  >
    {/* Globe with slow-spinning orbit ring */}
    <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
      <div className="absolute inset-0 rounded-full border border-dashed border-primary/40 animate-[spin_14s_linear_infinite]" />
      <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-sky-500 text-white shadow-md shadow-primary/30">
        <Globe className="h-5 w-5" strokeWidth={1.75} />
      </div>
      <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/70 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
      </span>
    </div>

    <div className="text-left leading-none">
      <div className="text-2xl font-semibold tracking-tight text-foreground">
        Nearshore
      </div>
      <div className="mt-1.5 text-[0.65rem] font-medium uppercase tracking-[0.24em] text-primary/80">
        Engineering Team
      </div>
    </div>
  </div>
);


const Hero = () => {
  // Set isVisible to false initially
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const heroRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Only for the Hero component, we want to trigger visibility immediately on component mount
    // This is appropriate for the Hero which is always visible on page load
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100); // Small delay for better visual effect
    
    // Cleanup on unmount
    return () => {
      clearTimeout(timer);
      // Reset the state when component unmounts
      setIsVisible(false);
      setHasAnimated(false);
    };
  }, []);

  const scrollToNextSection = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default anchor behavior
    const nextSection = document.getElementById('applications');
    if (nextSection) {
      // Get the header element to dynamically calculate its height
      const header = document.querySelector('header');
      // Use relative units (vh) with a fallback
      const headerHeight = header ? header.getBoundingClientRect().height : window.innerHeight * 0.06;
      
      // Calculate position considering header height
      const elementPosition = nextSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="home" 
      ref={heroRef}
      className="relative min-h-screen flex flex-col items-center justify-start pt-48 px-6 overflow-hidden"
    >
      {/* Cool animated background design covering 3/4 of section height */}
      <div className="absolute inset-0 overflow-hidden" style={{ height: '65%' }}>
        {/* Main gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/30 to-accent/20" />
        
        {/* Comprehensive geometric pattern overlay */}
        <div className="absolute inset-0">
          {/* Diagonal lines pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `
              linear-gradient(45deg, transparent 49%, hsl(var(--primary)) 49%, hsl(var(--primary)) 50%, transparent 50%),
              linear-gradient(-45deg, transparent 49%, hsl(var(--secondary)) 49%, hsl(var(--secondary)) 50%, transparent 50%)
            `,
            backgroundSize: '60px 60px'
          }} />
          
          {/* Triangular geometric pattern */}
          <div className="absolute inset-0 opacity-12" style={{
            backgroundImage: `
              linear-gradient(60deg, transparent 66%, hsl(var(--primary)/0.15) 66%, hsl(var(--primary)/0.15) 67%, transparent 67%),
              linear-gradient(120deg, transparent 66%, hsl(var(--secondary)/0.15) 66%, hsl(var(--secondary)/0.15) 67%, transparent 67%),
              linear-gradient(180deg, transparent 66%, hsl(var(--accent)/0.15) 66%, hsl(var(--accent)/0.15) 67%, transparent 67%)
            `,
            backgroundSize: '80px 80px, 80px 80px, 80px 80px',
            backgroundPosition: '0 0, 40px 40px, 20px 60px'
          }} />
        </div>
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
        
        {/* Gradient fade to white at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background via-background/80 to-transparent" />
      </div>
      
      {/* Enhanced background gradient spheres with better animations */}
      <div className="absolute top-40 -left-20 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-20 animate-subtle-pulse" />
      <div className="absolute -bottom-40 -right-20 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-20 animate-subtle-pulse" style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-secondary/20 rounded-full filter blur-3xl opacity-15 animate-subtle-pulse" style={{ animationDelay: '1s' }} />
      
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Nearshore badge: floats just past the end of the headline on wide screens */}
        <div
          className={cn(
            "hidden xl:block absolute top-[4.75rem] left-[calc(100%-7rem)] z-10",
            "transition-all duration-1000 delay-700",
            isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
          )}
        >
          <NearshoreBadge isVisible={isVisible} />
        </div>

        <div 
          className={cn(
            "transition-all duration-1000 transform",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          )}
        >
          <h1 className={cn(
            "text-4xl md:text-5xl lg:text-7xl font-medium mb-6 transition-all duration-1000",
            isVisible ? "opacity-100 tracking-normal" : "opacity-0 tracking-wider"
          )}>
            Innovation Through Intelligence
          </h1>

          {/* Nearshore badge: inline fallback for screens too narrow for the floating version */}
          <div className="flex justify-center mb-8 xl:hidden">
            <NearshoreBadge isVisible={isVisible} />
          </div>
          
          <p 
            className={cn(
              "text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 transition-all",
              isVisible 
                ? "opacity-100 translate-y-0 duration-1000 delay-300" 
                : "opacity-0 translate-y-10 duration-500"
            )}
          >
            We build intelligent software solutions that transform how businesses operate. 
            Our expertise in software development and AI creates technology that makes a difference.
          </p>
          
          <div 
            className={cn(
              "flex flex-col sm:flex-row gap-4 justify-center",
              isVisible 
                ? "opacity-100 translate-y-0 transition-all duration-1000 delay-500" 
                : "opacity-0 translate-y-10 transition-all duration-500"
            )}
          >
            <Link 
              to="/contact" 
              className={cn(
                "px-8 py-3 rounded-full bg-primary text-white font-medium shadow-lg transition-all",
                isVisible 
                  ? "hover:shadow-xl hover:bg-primary/90 hover:translate-y-[-2px]" 
                  : ""
              )}
            >
              {/* View Our Work */}
              Contact Us
            </Link>
            {/* <Link
              to="/contact" 
              className={cn(
                "px-8 py-3 rounded-full bg-secondary text-secondary-foreground font-medium transition-all",
                isVisible 
                  ? "hover:bg-secondary/80 hover:translate-y-[-2px]" 
                  : ""
              )}
            >
              Contact Us
            </Link> */}
          </div>
        </div>
      </div>

      {/* Three feature cards */}
      <div className="relative z-10 w-full max-w-8xl mx-auto mt-16 mb-20 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <Card 
            className={cn(
              "transition-all duration-1000 transform border-muted",
              isVisible 
                ? "opacity-100 translate-y-0 delay-700" 
                : "opacity-0 translate-y-10"
            )}
          >
            <CardHeader className="p-6 text-left">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Code className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg mb-2">Development</CardTitle>
              <CardDescription className="text-sm">
                Custom software solutions built with modern technologies
              </CardDescription>
            </CardHeader>
          </Card>

          <Card 
            className={cn(
              "transition-all duration-1000 transform border-muted",
              isVisible 
                ? "opacity-100 translate-y-0 delay-800" 
                : "opacity-0 translate-y-10"
            )}
          >
            <CardHeader className="p-6 text-left">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Lightbulb className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg mb-2">Innovation</CardTitle>
              <CardDescription className="text-sm">
                AI-powered solutions that drive business transformation
              </CardDescription>
            </CardHeader>
          </Card>

          <Card 
            className={cn(
              "transition-all duration-1000 transform border-muted",
              isVisible 
                ? "opacity-100 translate-y-0 delay-900" 
                : "opacity-0 translate-y-10"
            )}
          >
            <CardHeader className="p-6 text-left">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-lg mb-2">Collaboration</CardTitle>
              <CardDescription className="text-sm">
                Expert team dedicated to your success and growth
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
      
      <button
        onClick={scrollToNextSection}
        className={cn(
          "absolute bottom-10 left-1/2 transform -translate-x-1/2 text-muted-foreground transition-all duration-1000",
          isVisible 
            ? "opacity-100 animate-bounce hover:text-foreground" 
            : "opacity-0"
        )}
        style={{ transitionDelay: '1200ms' }}
        aria-label="Scroll down"
      >
        <ArrowDown size={32} />
      </button>
    </section>
  );
};

export default Hero;
